import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, ChevronUp, ChevronDown, Check, Pencil, X } from 'lucide-react';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  getCourses, 
  getCourseTemplates, 
  getReportCard,
  saveReportCard,
  getCriteriaGroups,
  saveCriteriaGroup,
  deleteCriteriaGroup,
  updateCriteriaGroup,
  getAcademicYears,
  executeWithRetry,
  verifyTemplateAssignment,
  getCenterConfig
} from '../../lib/db';
import { CustomSelect } from '../ui/CustomSelect';
import './ReportCardDesigner.css';
import { 
  CourseTemplate, 
  Course, 
  CourseTemplateSubject,
  Section,
  Field,
  CriteriaGroup,
  AcademicYear} from '../../types';
import { RichTextEditor } from '../common/RichTextEditor';

// Componente para una sección arrastrable
function DraggableSection({ 
  section, 
  onDelete, 
  onUpdateFields,
  onUpdateSection,
  centerConfig,
  getActiveAcademicYear,
  courses,
  selectedCourse,
  // @ts-ignore - savedGroups se usa indirectamente en handleDrop y handleSaveTemplate
  savedGroups,
  setSavedGroups
}: { 
  section: Section; 
  onDelete: (id: string) => void;
  onUpdateFields: (sectionId: string, fields: Field[]) => void;
  onUpdateSection: (section: Section) => void;
  centerConfig: { name: string; logo?: string } | null;
  getActiveAcademicYear: () => string;
  courses: Course[];
  selectedCourse: string;
  savedGroups: CriteriaGroup[];
  setSavedGroups: React.Dispatch<React.SetStateAction<CriteriaGroup[]>>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.id });

  const [newCriterionName, setNewCriterionName] = useState('');
  const [isAddingCriterion, setIsAddingCriterion] = useState(false);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [globalGrade, setGlobalGrade] = useState('');
  const [editingCriterionId, setEditingCriterionId] = useState<string | null>(null);
  const [editingCriterionName, setEditingCriterionName] = useState('');

  const handleDeleteCriterion = (fieldId: string) => {
    const updatedFields = section.fields.filter(field => field.id !== fieldId);
    onUpdateFields(section.id, updatedFields);
  };

  const handleAddCriterion = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCriterionName.trim()) {
      const newField: Field = {
        id: crypto.randomUUID(),
        name: newCriterionName.trim(),
        type: 'select'
      };
      onUpdateFields(section.id, [...section.fields, newField]);
      setNewCriterionName('');
      setIsAddingCriterion(false);
    }
  };

  // Función para guardar plantilla
  const handleSaveTemplate = async (e: React.FormEvent, section: Section) => {
    e.preventDefault();
    if (!templateName.trim()) return;

    try {
      const newGroup = await saveCriteriaGroup({
        name: templateName,
        criteria: section.fields.filter(f => f.type === 'select')
      });
      
      // Actualizar la lista de grupos inmediatamente
      setSavedGroups((prevGroups: CriteriaGroup[]) => [...prevGroups, newGroup]);
      
      setTemplateName('');
      setIsAddingTemplate(false);
    } catch (error) {
      console.error('Error al guardar la plantilla:', error);
    }
  };

  const handleDrop = (e: React.DragEvent, sectionId: string) => {
    e.preventDefault();
    console.log('Drop event triggered on section:', sectionId);
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      console.log('Raw JSON data:', jsonData);
      
      if (!jsonData) {
        console.error('No se recibieron datos JSON en el evento drop');
        return;
      }
      
      const data = JSON.parse(jsonData);
      console.log('Datos recibidos en drop:', data);
      
      if (data.type === 'criteria-template') {
        if (Array.isArray(data.data)) {
          // Crear nuevos campos con IDs únicos
          const newFields = data.data.map((criterion: any) => ({
            id: crypto.randomUUID(),
            name: criterion.name || 'Criterio sin nombre',
            type: 'select'
          }));
          
          console.log('Añadiendo campos:', newFields);
          
          // Actualizar los campos de la sección
          const updatedFields = [...section.fields, ...newFields];
          console.log('Campos actualizados:', updatedFields);
          
          onUpdateFields(sectionId, updatedFields);
        } else {
          console.error('data.data no es un array:', data.data);
        }
      }
    } catch (error) {
      console.error('Error al procesar el drop:', error);
    }
  };

  const renderSectionContent = () => {
    switch (section.type) {
      case 'header':
        return (
          <div className="p-6 mb-6 bg-white rounded-lg border border-gray-100 shadow-md">
            {/* Título */}
            <div className="mb-6 text-center">
              <h1 className="text-3xl font-bold text-gray-800 uppercase">Boletín de Calificaciones</h1>
            </div>

            {/* Contenedor principal del encabezado */}
            <div className="grid grid-cols-12 gap-4">
              {/* Logo a la izquierda */}
              <div className="col-span-3">
                {centerConfig?.logo ? (
                  <div className="relative w-40 h-40">
                    <img 
                      src={centerConfig.logo} 
                      alt="Logo del centro"
                      className="object-contain w-full h-full rounded-lg border"
                    />
                  </div>
                ) : (
                  <div className="flex justify-center items-center w-40 h-40 bg-gray-50 rounded-lg border-2 border-gray-300 border-dashed">
                    <span className="text-gray-500">Logo no disponible</span>
                  </div>
                )}
              </div>

              {/* Información central y derecha */}
              <div className="col-span-9 space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  {/* Centro */}
                  <div className="col-span-8">
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Centro
                    </label>
                    <div className="p-2 bg-gray-50 rounded border">
                      <span className="text-gray-800">
                        {centerConfig?.name || 'Centro no configurado'}
                      </span>
                    </div>
                  </div>

                  {/* Curso Académico */}
                  <div className="col-span-4">
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Curso Académico
                    </label>
                    <div className="p-2 bg-gray-50 rounded border">
                      <span className="text-gray-800">
                        {getActiveAcademicYear()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Apellidos y nombre - ancho completo */}
                <div className="w-full">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Apellidos y nombre
                  </label>
                  <div className="p-2 bg-blue-50 rounded border border-blue-200">
                    <span className="italic text-blue-600">{'{{apellidos}}, {{nombre}}'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'grades':
        // Usar savedGroups en una variable para que TypeScript detecte su uso
        const availableTemplates = savedGroups;
        console.log('Plantillas disponibles:', availableTemplates.length);
        
        return (
          <div className="space-y-4">
            {/* Línea principal con curso, faltas y calificación en una sola fila */}
            <div className="grid grid-cols-12 gap-4 items-center p-4 bg-gray-50 rounded-lg">
              <div className="col-span-5">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">CURSO</span>
                  <div className="flex-1 px-3 py-2 bg-white rounded-md border">
                    <span className="text-gray-800">
                      {courses.find((c: Course) => c.id === selectedCourse)?.name || ''}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-span-3">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">FALTAS ASISTENCIA</span>
                  <input
                    type="number"
                    min="0"
                    className="px-3 py-2 w-20 text-center rounded-md border focus:ring-2 focus:ring-indigo-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="col-span-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">CALIFICACIÓN GLOBAL</span>
                  <div className="flex-1">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={globalGrade}
                      onChange={(e) => setGlobalGrade(e.target.value)}
                      className="px-3 py-2 w-full bg-white rounded-md border"
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Criterios de evaluación - 2 por fila */}
            <div 
              className="grid grid-cols-2 gap-4 p-4 rounded-lg border-2 border-gray-300 border-dashed"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Evitar que el evento se propague
                e.currentTarget.classList.add('bg-indigo-50', 'border-indigo-300');
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('bg-indigo-50', 'border-indigo-300');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('bg-indigo-50', 'border-indigo-300');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Evitar que el evento se propague
                e.currentTarget.classList.remove('bg-indigo-50', 'border-indigo-300');
                handleDrop(e, section.id);
              }}
            >
              {/* Mensaje cuando no hay criterios */}
              {section.fields.filter(field => field.type === 'select').length === 0 && (
                <div className="col-span-2 p-4 text-center text-gray-500">
                  <p>Arrastra plantillas de criterios aquí o añade criterios individuales</p>
                </div>
              )}
              
              {/* Lista de criterios existentes */}
              {section.fields.filter(field => field.type === 'select').map((field) => (
                <div
                  key={field.id}
                  className="flex items-center gap-2 px-3 py-2 w-full bg-white rounded-lg border border-gray-200 group"
                >
                  {editingCriterionId === field.id ? (
                    <input
                      autoFocus
                      className="flex-1 text-sm px-1 py-0.5 rounded border border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      value={editingCriterionName}
                      onChange={e => setEditingCriterionName(e.target.value)}
                      onBlur={() => {
                        if (editingCriterionName.trim()) {
                          onUpdateFields(section.id, section.fields.map(f =>
                            f.id === field.id ? { ...f, name: editingCriterionName.trim() } : f
                          ));
                        }
                        setEditingCriterionId(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') { setEditingCriterionId(null); }
                      }}
                    />
                  ) : (
                    <span
                      className="flex-1 text-sm font-medium text-gray-700 cursor-text"
                      onDoubleClick={() => { setEditingCriterionId(field.id); setEditingCriterionName(field.name); }}
                    >
                      {field.name}
                    </span>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingCriterionId(field.id); setEditingCriterionName(field.name); }}
                      className="p-1 text-slate-400 hover:text-indigo-600"
                      title="Editar criterio"
                    ><Pencil className="w-3.5 h-3.5" /></button>
                    <button
                      onClick={() => handleDeleteCriterion(field.id)}
                      className="p-1 text-slate-400 hover:text-red-500"
                      title="Eliminar criterio"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Campo de observaciones individuales (por alumno) */}
            {section.fields.filter(field => field.type === 'text').map((field) => (
              <div 
                key={field.id}
                className="mt-4 space-y-2"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-700">
                      {field.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full border border-emerald-200">
                      Por alumno
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteCriterion(field.id)}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Eliminar observaciones"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <textarea
                    className="w-full min-h-[80px] p-3 bg-white rounded-md border border-emerald-200 focus:ring-2 focus:ring-emerald-400 resize-none text-sm"
                    placeholder="Observaciones individuales por alumno..."
                    rows={3}
                  />
                </div>
              </div>
            ))}

            {/* Observaciones generales de la asignatura (para toda la clase) */}
            {section.fields.filter(field => field.type === 'general_observations').map((field) => (
              <div key={field.id} className="mt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-700">
                      {field.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full border border-amber-200">
                      Para toda la clase
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteCriterion(field.id)}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Eliminar observaciones generales"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <textarea
                    className="w-full min-h-[80px] p-3 bg-white rounded-md border border-amber-200 focus:ring-2 focus:ring-amber-400 resize-none text-sm text-slate-700"
                    placeholder="Observaciones generales de la asignatura (comunes para todos los alumnos)..."
                    rows={3}
                    value={field.value || ''}
                    onChange={(e) => {
                      const updatedFields = section.fields.map(f =>
                        f.id === field.id ? { ...f, value: e.target.value } : f
                      );
                      onUpdateFields(section.id, updatedFields);
                    }}
                  />
                </div>
              </div>
            ))}

            {/* Botones de acción */}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => setIsAddingCriterion(true)}
                className="flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 border border-indigo-200"
              >
                <Plus className="mr-1.5 w-3.5 h-3.5" />
                Añadir criterio
              </button>

              {/* Botón de observaciones individuales */}
              {!section.fields.some(field => field.type === 'text') && (
                <button
                  onClick={() => onUpdateFields(section.id, [
                    ...section.fields,
                    {
                      id: crypto.randomUUID(),
                      name: 'Observaciones',
                      type: 'text'
                    }
                  ])}
                  className="flex items-center px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-md hover:bg-emerald-100 border border-emerald-200"
                >
                  <Plus className="mr-1.5 w-3.5 h-3.5" />
                  Obs. individuales
                </button>
              )}

              {/* Botón de observaciones generales */}
              {!section.fields.some(field => field.type === 'general_observations') && (
                <button
                  onClick={() => onUpdateFields(section.id, [
                    ...section.fields,
                    {
                      id: crypto.randomUUID(),
                      name: 'Observaciones del Profesor',
                      type: 'general_observations'
                    }
                  ])}
                  className="flex items-center px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-md hover:bg-amber-100 border border-amber-200"
                >
                  <Plus className="mr-1.5 w-3.5 h-3.5" />
                  Obs. generales
                </button>
              )}

              {/* Botón de guardar plantilla */}
              <button
                onClick={() => setIsAddingTemplate(true)}
                className="flex items-center px-3 py-1.5 text-xs font-medium text-violet-600 bg-violet-50 rounded-md hover:bg-violet-100 border border-violet-200"
              >
                <Save className="mr-1.5 w-3.5 h-3.5" />
                Guardar plantilla
              </button>
            </div>

            {/* Modal para añadir criterio */}
            {isAddingCriterion && (
              <div className="p-4 mt-4 bg-gray-50 rounded-lg border">
                <form onSubmit={handleAddCriterion}>
                  <input
                    type="text"
                    value={newCriterionName}
                    onChange={(e) => setNewCriterionName(e.target.value)}
                    className="block px-3 py-2 w-full rounded-md border"
                    placeholder="Nombre del criterio"
                  />
                  <div className="flex justify-end mt-3 space-x-2">
                    <button
                      type="button"
                      onClick={() => setIsAddingCriterion(false)}
                      className="px-3 py-1 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-3 py-1 text-white bg-indigo-600 rounded hover:bg-indigo-700"
                    >
                      Añadir
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        );

      case 'observations':
        return (
          <div className="p-4">
            <RichTextEditor 
              initialValue={section.data?.content || ''}
              onChange={(html) => {
                const updatedSection = { ...section };
                if (!updatedSection.data) {
                  updatedSection.data = {};
                }
                updatedSection.data.content = html;
                onUpdateSection(updatedSection);
              }}
              placeholder="Escribe el contenido aquí..."
            />
          </div>
        );

      case 'signatures':
        return (
          <div className="grid grid-cols-2 gap-8 p-2">
            {(['teacher', 'director'] as const).map((role) => {
              const label = role === 'teacher' ? 'Firma del Profesor' : 'Firma del Director';
              const fieldKey = role === 'teacher' ? 'teacherSignature' : 'directorSignature';
              const currentValue = (section.data as any)?.[fieldKey] as string | undefined;
              return (
                <div key={role} className="flex flex-col items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
                  <div className="w-full h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden">
                    {currentValue ? (
                      <>
                        <img src={currentValue} alt={label} className="max-h-full max-w-full object-contain p-2" />
                        <button
                          onClick={() => {
                            const updated = { ...section, data: { ...(section.data || {}), [fieldKey]: undefined } };
                            onUpdateSection(updated);
                          }}
                          className="absolute top-1 right-1 p-1 bg-white rounded-full shadow text-red-400 hover:text-red-600"
                          title="Eliminar firma"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <label className="flex flex-col items-center gap-1 cursor-pointer text-slate-400 hover:text-indigo-500 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs">Subir imagen</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              const dataUrl = ev.target?.result as string;
                              const updated = { ...section, data: { ...(section.data || {}), [fieldKey]: dataUrl } };
                              onUpdateSection(updated);
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                      </label>
                    )}
                  </div>
                  <div className="w-full pt-2 border-t border-slate-300">
                    <p className="text-center text-xs text-slate-400 italic">Fdo. ___________________________</p>
                  </div>
                </div>
              );
            })}
          </div>
        );
    }
  };

  const sectionHeaderColors = {
    header:       { bg: 'bg-sky-50',     border: 'border-sky-100',     dot: 'bg-sky-400',     label: 'text-sky-700'     },
    grades:       { bg: 'bg-slate-50',   border: 'border-slate-200',   dot: 'bg-indigo-400',  label: 'text-slate-700'   },
    observations: { bg: 'bg-emerald-50', border: 'border-emerald-100', dot: 'bg-emerald-400', label: 'text-emerald-700' },
    signatures:   { bg: 'bg-violet-50',  border: 'border-violet-100',  dot: 'bg-violet-400',  label: 'text-violet-700'  },
  };
  const colors = sectionHeaderColors[section.type as keyof typeof sectionHeaderColors] || sectionHeaderColors.grades;

  return (
    <div
      ref={setNodeRef}
      className={`
        draggable-section
        draggable-section-container
        ${isDragging ? 'draggable-section-dragging' : 'draggable-section-normal'}
      `}
      style={{
        '--transform': CSS.Transform.toString(transform),
        '--transition': transition
      } as React.CSSProperties}
    >
      <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 shadow-sm bg-white">
        <div className={`flex items-center justify-between px-4 py-3 border-b ${colors.bg} ${colors.border}`}>
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="p-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing rounded"
            >
              <GripVertical className="w-4 h-4" />
            </button>
            <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
            <h3 className={`text-sm font-semibold uppercase tracking-wide ${colors.label}`}>{section.title}</h3>
          </div>
          <button
            className="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors"
            onClick={() => onDelete(section.id)}
            title="Eliminar sección"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          {renderSectionContent()}
        </div>
      </div>

      {/* Modal para guardar plantilla */}
      {isAddingTemplate && (
        <div className="flex fixed inset-0 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 bg-white rounded-lg shadow-xl">
            <h3 className="mb-4 text-lg font-medium text-gray-900">
              Guardar plantilla de criterios
            </h3>
            <form onSubmit={(e) => handleSaveTemplate(e, section)}>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="block px-3 py-2 w-full rounded-md border"
                placeholder="Nombre de la plantilla"
              />
              <div className="flex justify-end mt-4 space-x-2">
                <button
                  type="button"
                  onClick={() => setIsAddingTemplate(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-purple-600 rounded hover:bg-purple-700"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function ReportCardDesigner() {
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [courses, setCourses] = useState<Course[]>([]);
  const [templates, setTemplates] = useState<CourseTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<Section[]>([]);
  const [savedGroups, setSavedGroups] = useState<CriteriaGroup[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [currentTemplateSubjects, setCurrentTemplateSubjects] = useState<CourseTemplateSubject[]>([]);
  const [centerConfig, setCenterConfig] = useState<{name: string; logo?: string} | null>(null);
  const [saveStatus, setSaveStatus] = useState<'unsaved' | 'saving' | 'saved'>('saved');
  const [hasChanges, setHasChanges] = useState(false);
  const [reportCardId, setReportCardId] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [originalSections, setOriginalSections] = useState<Section[]>([]);
  const [originalSubjects, setOriginalSubjects] = useState<CourseTemplateSubject[]>([]);
  const [editingGroup, setEditingGroup] = useState<CriteriaGroup | null>(null);

  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  }, []);

  // Cargar datos cuando cambia el curso
  useEffect(() => {
    console.log("useEffect: selectedCourse changed to", selectedCourse);
    if (selectedCourse) {
      loadReportCard();
    } else {
      setSections([]);
      setCurrentTemplateSubjects([]);
    }
  }, [selectedCourse, courses, templates]);

  // Cargar grupos de criterios
  useEffect(() => {
    getCriteriaGroups()
      .then(groups => {
        console.log('Criterios cargados:', groups);
        setSavedGroups(groups);
      })
      .catch(console.error);
  }, []);

  // Añadir useEffect para cargar la configuración del centro
  useEffect(() => {
    const loadCenterConfig = async () => {
      try {
        const config = await getCenterConfig();
        setCenterConfig(config);
      } catch (error) {
        console.error('Error loading center config:', error);
      }
    };

    loadCenterConfig();
  }, []);

  // Función para obtener el año académico activo
  const getActiveAcademicYear = () => {
    return academicYears.find(year => year.isActive)?.name || '';
  };

  // Modificar loadData para cargar también los años académicos
  async function loadData() {
    try {
      const [coursesData, templatesData, academicYearsData] = await Promise.all([
        getCourses(),
        getCourseTemplates(),
        getAcademicYears()
      ]);
      setCourses(coursesData);
      setTemplates(templatesData);
      setAcademicYears(academicYearsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }

  const addSection = (type: 'header' | 'grades' | 'observations' | 'signatures') => {
    const newSection: Section = {
      id: crypto.randomUUID(),
      title: type === 'header' ? 'Encabezado' :
            type === 'grades' ? 'Calificaciones' :
            type === 'observations' ? 'Observaciones' : 'Firmas',
      type,
      fields: []
    };
    setSections([...sections, newSection]);
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  const deleteSection = (id: string) => {
    setSections(sections.filter(s => s.id !== id));
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      
      const newSections = [...sections];
      const [movedSection] = newSections.splice(oldIndex, 1);
      newSections.splice(newIndex, 0, movedSection);
      
      setSections(newSections);
    }
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  const updateSectionFields = (sectionId: string, fields: Field[]) => {
    setSections(sections.map(section => 
      section.id === sectionId 
        ? { ...section, fields }
        : section
    ));
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  const loadReportCard = async () => {
    try {
      const currentCourse = courses.find(c => c.id === selectedCourse);
      const activeAcademicYear = getActiveAcademicYear();

      if (currentCourse) {
        await verifyTemplateAssignment(selectedCourse);
        
        // Buscar la plantilla
        let template = templates.find(t => t.id === currentCourse.templateId);
        if (!template) {
          template = templates.find(t =>
            t.courseId === currentCourse.id &&
            t.academicYear === activeAcademicYear
          );
        }

        if (template) {
          // Establecer las asignaturas de la plantilla
          const templateSubjects = template.subjects;
          setCurrentTemplateSubjects(templateSubjects);
          setOriginalSubjects(JSON.parse(JSON.stringify(templateSubjects))); // Copia profunda
          
          const reportCard = await executeWithRetry(() => getReportCard(selectedCourse));
          if (reportCard) {
            setSections(reportCard.sections);
            setOriginalSections(JSON.parse(JSON.stringify(reportCard.sections))); // Copia profunda
            setReportCardId(reportCard.id);
          } else {
            setSections([]);
            setOriginalSections([]);
            setReportCardId(null);
          }
        } else {
          setCurrentTemplateSubjects([]);
          setOriginalSubjects([]);
          setSections([]);
          setOriginalSections([]);
          setReportCardId(null);
        }
        
        // Marcar la carga inicial como completa y resetear hasChanges
        setInitialLoadComplete(true);
        setHasChanges(false);
        setSaveStatus('saved');
      }
    } catch (error) {
      console.error('Error loading report card:', error);
    }
  };

  const checkForChanges = () => {
    // Comparar secciones
    const sectionsChanged = JSON.stringify(sections) !== JSON.stringify(originalSections);
    
    // Comparar asignaturas
    const subjectsChanged = JSON.stringify(currentTemplateSubjects) !== JSON.stringify(originalSubjects);
    
    // Actualizar el estado de cambios
    const hasAnyChanges = sectionsChanged || subjectsChanged;
    setHasChanges(hasAnyChanges);
    setSaveStatus(hasAnyChanges ? 'unsaved' : 'saved');
  };

  const handleSave = async () => {
    if (!selectedCourse || !hasChanges) return;

    try {
      setSaveStatus('saving');
      
      // Obtener el curso actual
      const currentCourse = courses.find(c => c.id === selectedCourse);
      if (!currentCourse) {
        throw new Error('No se encontró el curso');
      }
      
      // Buscar la plantilla existente o crear un ID temporal
      const template = templates.find(t => 
        t.courseId === selectedCourse && 
        t.academicYear === getActiveAcademicYear()
      );
      
      // Si no hay plantilla, necesitamos crear una o asignar una existente
      let templateId = template?.id;
      
      if (!templateId) {
        console.log('No se encontró plantilla para el curso, creando una temporal...');
        // Generar un ID temporal para la plantilla
        templateId = `temp-template-${crypto.randomUUID()}`;
        
        // Actualizar el curso con el nuevo templateId
        try {
          console.log('Actualizando curso con templateId:', templateId);
          
          // Si verifyTemplateAssignment solo acepta un argumento, usamos otra función
          // o actualizamos el curso de otra manera
          await verifyTemplateAssignment(selectedCourse);
          
          // Aquí podríamos necesitar una función adicional para actualizar el templateId
          // Por ejemplo:
          // await updateCourseTemplate(selectedCourse, templateId);
        } catch (error) {
          console.error('Error al asignar plantilla al curso:', error);
        }
      }
      
      console.log('Guardando boletín con templateId:', templateId);
      
      // Guardar el boletín con un ID y el templateId
      await saveReportCard({
        id: reportCardId || crypto.randomUUID(),
        courseId: selectedCourse,
        sections,
        subjects: currentTemplateSubjects
      }, templateId);
      
      // Actualizar los originales después de guardar
      setOriginalSections(JSON.parse(JSON.stringify(sections)));
      setOriginalSubjects(JSON.parse(JSON.stringify(currentTemplateSubjects)));
      
      setSaveStatus('saved');
      setHasChanges(false);
      
      console.log('Boletín guardado correctamente');
      
      // Recargar los cursos para actualizar la información de templateId
      const updatedCourses = await getCourses();
      setCourses(updatedCourses);
      
    } catch (error) {
      console.error('Error al guardar el boletín:', error);
      setSaveStatus('unsaved');
    }
  };

  // Modificar el useEffect para actualizar hasChanges cuando cambia algo
  useEffect(() => {
    if (sections.length > 0 && !loading) {
      setHasChanges(true);
      setSaveStatus('unsaved');
    }
  }, [sections, currentTemplateSubjects]);

  const updateSection = (updatedSection: Section) => {
    setSections(sections.map(section => 
      section.id === updatedSection.id 
        ? updatedSection
        : section
    ));
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  // Añadir una función para manejar la navegación
  const handleNavigation = (action: () => void) => {
    if (hasChanges) {
      setPendingAction(() => action);
      setIsConfirmationModalOpen(true);
    } else {
      action();
    }
  };

  // Añadir un useEffect para detectar cuando el usuario intenta salir de la página
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges]);

  // Añadir un useEffect para que solo se active después de la carga inicial
  useEffect(() => {
    if (initialLoadComplete) {
      checkForChanges();
    }
  }, [sections, currentTemplateSubjects, initialLoadComplete]);

  // Añadir un componente para el modal de confirmación
  const ConfirmationModal = () => {
    if (!isConfirmationModalOpen) return null;

    return (
      <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
        <div className="p-6 max-w-md bg-white rounded-lg shadow-xl">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Cambios sin guardar</h3>
          <p className="mb-6 text-gray-600">
            Hay cambios sin guardar. ¿Quieres guardar los cambios antes de continuar?
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => {
                setIsConfirmationModalOpen(false);
                if (pendingAction) pendingAction();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              No guardar
            </button>
            <button
              onClick={() => {
                setIsConfirmationModalOpen(false);
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                await handleSave();
                setIsConfirmationModalOpen(false);
                if (pendingAction) pendingAction();
              }}
              className="px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteCriteriaGroup(groupId);
      setSavedGroups(savedGroups.filter(group => group.id !== groupId));
    } catch (error) {
      console.error('Error deleting criteria group:', error);
    }
  };

  const moveSection = (id: string, dir: 'up' | 'down') => {
    const idx = sections.findIndex(s => s.id === id);
    if (dir === 'up' && idx === 0) return;
    if (dir === 'down' && idx === sections.length - 1) return;
    const next = [...sections];
    const target = dir === 'up' ? idx - 1 : idx + 1;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSections(next);
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  const addSubjectAsSection = (subject: CourseTemplateSubject) => {
    const newSection: Section = {
      id: crypto.randomUUID(),
      title: subject.name,
      type: 'grades',
      fields: []
    };
    setSections(prev => [...prev, newSection]);
    setHasChanges(true);
    setSaveStatus('unsaved');
  };

  const handleUpdateGroup = async (updated: CriteriaGroup) => {
    await updateCriteriaGroup(updated);
    setSavedGroups(savedGroups.map(g => g.id === updated.id ? updated : g));
    setEditingGroup(null);
  };

  return (
    <div>
      {/* Barra superior — sticky, siempre visible al hacer scroll */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <label htmlFor="course-select" className="text-sm font-semibold text-slate-600 whitespace-nowrap">
            Curso
          </label>
          <CustomSelect
            id="course-select"
            value={selectedCourse}
            onValueChange={(v) => handleNavigation(() => setSelectedCourse(v))}
            options={[...courses]
              .sort((a, b) => {
                const levelCmp = (a.levelName || '').localeCompare(b.levelName || '');
                if (levelCmp !== 0) return levelCmp;
                return a.name.localeCompare(b.name, undefined, { numeric: true });
              })
              .map(course => ({
              value: course.id,
              label: `${course.name} - ${course.levelName} - ${course.academicYear}`
            }))}
            placeholder="Seleccione un curso..."
            className="w-80"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saveStatus === 'saving'}
          className={`
            flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 shrink-0
            ${saveStatus === 'saving'
              ? 'bg-blue-100 text-blue-700 cursor-wait'
              : saveStatus === 'saved'
                ? 'bg-green-100 text-green-700 cursor-default'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
            }
            ${!hasChanges ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {saveStatus === 'saving' ? (
            <><div className="w-4 h-4 rounded-full border-2 border-blue-600 animate-spin border-t-transparent" />Guardando...</>
          ) : saveStatus === 'saved' ? (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>Guardado</>
          ) : (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>Guardar Boletín</>
          )}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loading-spinner" />
        </div>
      ) : (
        <div className="flex gap-6 p-6 items-start">
          {/* Canvas izquierdo — crece y hace scroll naturalmente */}
          <div
            className="flex-1 min-w-0"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
            onDrop={(e) => {
              e.preventDefault();
              try {
                const data = JSON.parse(e.dataTransfer.getData('application/json'));
                if (data.type === 'subject') {
                  const newSection: Section = {
                    id: crypto.randomUUID(),
                    title: data.data.name,
                    type: 'grades',
                    fields: []
                  };
                  setSections([...sections, newSection]);
                } else if (data.type === 'criteriaGroup') {
                  const activeSection = sections.find(s => s.type === 'grades');
                  if (activeSection) {
                    const updatedFields = data.data.map((criterion: Field) => ({
                      ...criterion,
                      id: crypto.randomUUID()
                    }));
                    updateSectionFields(activeSection.id, updatedFields);
                  }
                }
              } catch (error) {
                console.error('Error al procesar el drop:', error);
              }
            }}
          >
            {!selectedCourse ? (
              <div className="flex flex-col items-center justify-center py-32 text-slate-400">
                <svg className="w-16 h-16 mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm">Selecciona un curso para diseñar su boletín</p>
              </div>
            ) : (
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  {sections.map((section) => (
                    <DraggableSection
                      key={section.id}
                      section={section}
                      onDelete={deleteSection}
                      onUpdateFields={updateSectionFields}
                      onUpdateSection={updateSection}
                      centerConfig={centerConfig}
                      getActiveAcademicYear={getActiveAcademicYear}
                      courses={courses}
                      selectedCourse={selectedCourse}
                      savedGroups={savedGroups}
                      setSavedGroups={setSavedGroups}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Panel derecho — sticky, siempre visible */}
          <div className="w-72 shrink-0 sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto space-y-3">

            {/* ── SECCIONES ACTUALES (visor + reordenador) ── */}
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Secciones</h2>
              {!selectedCourse || sections.length === 0 ? (
                <p className="text-xs text-slate-400 italic">{selectedCourse ? 'Sin secciones aún' : 'Selecciona un curso'}</p>
              ) : (
                <div className="space-y-1">
                  {sections.map((s, idx) => {
                    const dotColors: Record<string, string> = {
                      header: 'bg-sky-400', grades: 'bg-indigo-400',
                      observations: 'bg-emerald-400', signatures: 'bg-violet-400'
                    };
                    return (
                      <div key={s.id} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 group">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${dotColors[s.type] ?? 'bg-slate-400'}`} />
                        <span className="text-xs font-medium text-slate-700 truncate flex-1">{s.title}</span>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => moveSection(s.id, 'up')}
                            disabled={idx === 0}
                            className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-20 disabled:cursor-not-allowed"
                            title="Subir"
                          ><ChevronUp className="w-3.5 h-3.5" /></button>
                          <button
                            onClick={() => moveSection(s.id, 'down')}
                            disabled={idx === sections.length - 1}
                            className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-20 disabled:cursor-not-allowed"
                            title="Bajar"
                          ><ChevronDown className="w-3.5 h-3.5" /></button>
                          <button
                            onClick={() => deleteSection(s.id)}
                            className="p-0.5 rounded text-slate-400 hover:text-red-500"
                            title="Eliminar"
                          ><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── AÑADIR SECCIÓN ── */}
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Añadir sección</h2>

              {/* Bloques especiales */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => addSection('header')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-sky-700 bg-sky-50 rounded-lg hover:bg-sky-100 border border-sky-200 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Encabezado
                </button>
                <button
                  onClick={() => addSection('signatures')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 rounded-lg hover:bg-violet-100 border border-violet-200 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Firmas
                </button>
              </div>

              {/* Asignaturas — click para añadir */}
              {!selectedCourse ? (
                <p className="text-xs text-slate-400 italic">Selecciona un curso</p>
              ) : currentTemplateSubjects.length === 0 ? (
                <p className="text-xs text-slate-400">Sin asignaturas configuradas.</p>
              ) : (
                <>
                  <p className="text-xs text-slate-400 mb-1.5">Asignaturas del curso:</p>
                  <div className="space-y-1">
                    {currentTemplateSubjects.map(subject => {
                      const alreadyAdded = sections.some(
                        s => s.type === 'grades' && s.title === subject.name
                      );
                      return (
                        <button
                          key={subject.id}
                          onClick={() => addSubjectAsSection(subject)}
                          className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-colors ${
                            alreadyAdded
                              ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700'
                          }`}
                          title={alreadyAdded ? 'Ya añadida (click para añadir otra)' : 'Añadir como sección'}
                        >
                          {alreadyAdded
                            ? <Check className="w-3.5 h-3.5 shrink-0 text-indigo-500" />
                            : <Plus className="w-3.5 h-3.5 shrink-0" />}
                          <span className="text-xs font-medium truncate flex-1">{subject.name}</span>
                          <span className="text-xs text-slate-400 bg-slate-200 rounded-full px-1.5 py-0.5 shrink-0">
                            {subject.type === 'individual' ? 'Ind.' : 'Grp.'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* ── PLANTILLAS DE CRITERIOS ── */}
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Plantillas de criterios</h2>
              {savedGroups.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No hay plantillas guardadas</p>
              ) : (
                <div className="space-y-1.5">
                  {savedGroups.map(group => (
                    <div
                      key={group.id}
                      className="flex items-center gap-1.5 p-2.5 bg-white rounded-lg border border-slate-200 cursor-grab hover:bg-slate-50 transition-colors group"
                      draggable
                      onDragStart={(e) => {
                        const criteriaData = group.criteria.map(c => ({ id: c.id, name: c.name, type: 'select' }));
                        e.dataTransfer.setData('application/json', JSON.stringify({ type: 'criteria-template', data: criteriaData }));
                        e.dataTransfer.effectAllowed = 'copy';
                        const dragImage = document.createElement('div');
                        dragImage.textContent = group.name;
                        dragImage.style.cssText = 'padding:8px;background:#f3f4f6;border:1px solid #d1d5db;border-radius:4px;position:absolute;top:-1000px';
                        document.body.appendChild(dragImage);
                        e.dataTransfer.setDragImage(dragImage, 0, 0);
                        setTimeout(() => document.body.removeChild(dragImage), 0);
                      }}
                    >
                      <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-xs font-medium text-slate-700 truncate flex-1">{group.name}</span>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingGroup({ ...group, criteria: group.criteria.map(c => ({ ...c })) })}
                          className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                          title="Editar plantilla"
                        ><Pencil className="w-3.5 h-3.5" /></button>
                        <button
                          onClick={() => handleDeleteGroup(group.id)}
                          className="p-1 text-slate-400 hover:text-red-500 rounded"
                          title="Eliminar plantilla"
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal de edición de plantilla */}
            {editingGroup && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800">Editar plantilla</h3>
                    <button onClick={() => setEditingGroup(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* Nombre */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Nombre de la plantilla</label>
                      <input
                        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        value={editingGroup.name}
                        onChange={e => setEditingGroup({ ...editingGroup, name: e.target.value })}
                      />
                    </div>
                    {/* Criterios */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Criterios</label>
                      <div className="space-y-1.5 max-h-60 overflow-y-auto">
                        {editingGroup.criteria.map((c, idx) => (
                          <div key={c.id} className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-5 shrink-0">{idx + 1}.</span>
                            <input
                              className="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                              value={c.name}
                              onChange={e => setEditingGroup({
                                ...editingGroup,
                                criteria: editingGroup.criteria.map(x => x.id === c.id ? { ...x, name: e.target.value } : x)
                              })}
                            />
                            <button
                              onClick={() => setEditingGroup({
                                ...editingGroup,
                                criteria: editingGroup.criteria.filter(x => x.id !== c.id)
                              })}
                              className="p-1 text-slate-400 hover:text-red-500 shrink-0"
                            ><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => setEditingGroup({
                          ...editingGroup,
                          criteria: [...editingGroup.criteria, { id: crypto.randomUUID(), name: '', type: 'select' }]
                        })}
                        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 border border-indigo-200"
                      ><Plus className="w-3.5 h-3.5" /> Añadir criterio</button>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
                    <button
                      onClick={() => setEditingGroup(null)}
                      className="px-4 py-2 text-sm text-slate-600 bg-white rounded-lg border border-slate-200 hover:bg-slate-50"
                    >Cancelar</button>
                    <button
                      onClick={() => handleUpdateGroup(editingGroup)}
                      className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >Guardar cambios</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      <ConfirmationModal />
    </div>
  );
} 